import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  OvieCallableError,
  OvieHealthCallableService,
} from '@principle-theorem/ng-ai-suite';
import {
  BasicDialogService,
  DialogPresets,
  NgDialogModule,
} from '@principle-theorem/ng-shared';
import {
  type IAIReceptionistConfig,
  type IPractice,
  OvieHealthKnowledgeBaseAction,
  OvieHealthSettingsAction,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, type WithRef } from '@principle-theorem/shared';
import { ovieHealthErrorMessage } from '../tools/ovie-health-error-message';
import {
  type IViewDataDialogData,
  ViewDataDialogComponent,
} from '../view-data-dialog/view-data-dialog.component';

export interface IOvieHealthDialogData {
  practice: WithRef<IPractice>;
}

@Component({
  selector: 'pt-ovie-health-dialog',
  templateUrl: './ovie-health-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, MatSlideToggleModule, MatProgressSpinnerModule],
})
export class OvieHealthDialogComponent {
  private readonly _ovie = inject(OvieHealthCallableService);
  private readonly _dialog = inject(BasicDialogService);
  private readonly _snackBar = inject(MatSnackBar);

  private readonly _data = inject<IOvieHealthDialogData>(MAT_DIALOG_DATA);

  /**
   * Running copy of the persisted AI Receptionist config, seeded from the
   * snapshot passed into the dialog. Each successful patch advances this so a
   * later toggle preserves the value written by an earlier one (the dialog
   * holds a static practice snapshot that never re-emits).
   */
  private _aiReceptionist: Partial<IAIReceptionistConfig> = {
    ...this._data.practice.settings?.aiReceptionist,
  };

  readonly practiceName = this._data.practice.name;
  readonly enabled = signal(this._aiReceptionist.enabled ?? false);
  readonly testModeEnabled = signal(
    this._aiReceptionist.testModeEnabled ?? false
  );
  readonly saving = signal(false);
  readonly fetching = signal(false);

  async toggleEnabled(enabled: boolean): Promise<void> {
    if (await this._patchAiReceptionist({ enabled })) {
      this.enabled.set(enabled);
    }
  }

  async toggleTestMode(testModeEnabled: boolean): Promise<void> {
    if (await this._patchAiReceptionist({ testModeEnabled })) {
      this.testModeEnabled.set(testModeEnabled);
    }
  }

  async viewRawData(): Promise<void> {
    this.fetching.set(true);
    try {
      const [knowledgeBase, settings] = await Promise.all([
        this._ovie.knowledgeBase({
          action: OvieHealthKnowledgeBaseAction.GetKnowledgeBase,
          practiceRef: this._data.practice.ref,
        }),
        this._ovie.settings({
          action: OvieHealthSettingsAction.GetSettings,
          practiceRef: this._data.practice.ref,
        }),
      ]);
      const data: IViewDataDialogData = {
        title: `Ovie Health Data — ${this.practiceName}`,
        data: {
          knowledgeBase: knowledgeBase.data,
          settings: settings.data,
        },
      };
      this._dialog.open(ViewDataDialogComponent, DialogPresets.large({ data }));
    } catch (error) {
      const message =
        error instanceof OvieCallableError
          ? ovieHealthErrorMessage(error)
          : 'Unexpected error fetching Ovie Health data. Please try again.';
      this._snackBar.open(message, 'Dismiss', { duration: 6000 });
    } finally {
      this.fetching.set(false);
    }
  }

  /**
   * Persists a partial AI Receptionist config change. The bound toggle signal
   * is only advanced by the caller once this resolves — there is no optimistic
   * flip. On failure the signal is left untouched and an error snackbar is
   * shown. Returns whether the write succeeded.
   */
  private async _patchAiReceptionist(
    config: Partial<IAIReceptionistConfig>
  ): Promise<boolean> {
    this.saving.set(true);
    const nextConfig: Partial<IAIReceptionistConfig> = {
      ...this._aiReceptionist,
      ...config,
    };
    try {
      await Firestore.patchDoc(this._data.practice.ref, {
        settings: {
          ...this._data.practice.settings,
          aiReceptionist: nextConfig,
        },
      });
      this._aiReceptionist = nextConfig;
      return true;
    } catch {
      this._snackBar.open(
        'Could not update AI Receptionist settings. Please try again.',
        'Dismiss',
        { duration: 6000 }
      );
      return false;
    } finally {
      this.saving.set(false);
    }
  }
}
